/**
 * Turns a generated `Maze` and the profile's grounds block into the plain boxes an
 * outdoor level is made of: hedges, a moat, the bank that stops the player, and the
 * sensor outside the exit.
 *
 * The maze is centred on the origin, one grid line every `CELL_SIZE` metres, north at
 * -z (the exit side) and south at +z (the entry side) — the convention `generateMaze`
 * already uses. Hedge boxes are centred *on* their grid line, so the thickness straddles
 * it and the corridor between two lines is `CELL_SIZE - HEDGE_THICKNESS` wide.
 *
 * Two hedges stand outside the maze rather than on a grid line — the apron spurs below,
 * which cut the ring of grass so the maze cannot simply be walked around. A level may ask for
 * three more: the start courtyard, which walls the entry apron in around the spawn so the player
 * begins inside the labyrinth, with the maze's own entry gap as the only way out of the yard.
 *
 * Collinear hedges are merged into one box per run, the way `buildWalls` cuts a wall
 * into as few segments as the openings allow: a 12×12 maze is ~150 boxes instead of
 * ~500, which is what keeps one instanced draw cheap and the collider set small. Runs
 * meeting at a junction overlap by half a thickness — cheaper than authoring corner
 * pieces, and invisible inside a solid hedge. The entry and exit gaps need no special
 * case: the maze data already has those segments down, so no run covers them.
 *
 * Pure and headless, like the rest of `src/game/`.
 */

import { CREST_MARGIN, OUT_OF_BOUNDS_BOTTOM, type OutOfBounds } from '../bounds';
import type { BuiltGrounds } from '../profile/buildGeometry';
import type { Box, GroundsSpec, PathSegment, Rect } from '../profile/types';
import type { Maze } from './generateMaze';
import { CELL_SIZE, HEDGE_THICKNESS } from './grid';
import { placeLadders } from './placeLadders';
import { placeTables } from './placeTables';

// Re-exported so every caller still reads the grid off this module. They are declared in
// `grid.ts` because `placeLadders` below needs them too — see the note there.
export { CELL_SIZE, HEDGE_THICKNESS } from './grid';

/** Grass between the outer hedge and the bank, on every side. */
export const APRON_WIDTH = 6;

/**
 * How many cells across the start courtyard is — the walled yard a level may ask for on its
 * entry apron, so that the player begins inside the hedge instead of walking up to it.
 *
 * Three, because the apron is six metres deep: a one-cell yard in front of that depth is a
 * corridor rather than a yard. Three is also what makes a hand-authored spawn safe without a
 * second coordinate rule — validation already pins the spawn to the entry gap's own column, and
 * three cells give that column a clear cell of margin on either side.
 */
export const COURTYARD_CELLS = 3;

/** How far the water surface sits below the grass. */
export const WATER_DROP = 0.8;

/** Well above the 0.9 m jump apex: the bank is a stop, not a hurdle. */
export const BARRIER_HEIGHT = 4;
export const BARRIER_THICKNESS = 0.5;

/**
 * How deep a portal gate's volume is, straddling the hedge line it stands in. A walk
 * covers 5 cm a frame, so the hedge's own 0.6 m would already be caught — a metre is
 * still caught after a frame that ran long.
 */
export const GATE_DEPTH = 1;

/**
 * How far into the maze a player who has walked through a gate is put down, measured
 * from the outer hedge line: the middle of the first cell. Clear of the gate's own
 * volume — arriving inside it would send them straight back — and never on a grid line,
 * where a hedge may be standing.
 */
export const GATE_CLEARANCE = CELL_SIZE / 2;

/**
 * No sightline over a hedge, standing or jumping. The crest a player looks at is up to
 * `HEDGE_TOP_INSET` (5%) below this, so 3 m puts the deepest dip at 2.85 m — a clear
 * 25 cm above the 2.6 m a jumping player's eyes reach.
 */
export const DEFAULT_HEDGE_HEIGHT = 3;
export const DEFAULT_MOAT_WIDTH = 6;

/** Thin enough to read as a surface rather than a slab seen edge-on. */
const WATER_THICKNESS = 0.1;

/** The grass the player can stand on: the maze plus its apron, centred on the origin. */
export function groundsRect(columns: number, rows: number): Rect {
  const width = columns * CELL_SIZE + 2 * APRON_WIDTH;
  const depth = rows * CELL_SIZE + 2 * APRON_WIDTH;
  return { x: -width / 2, z: -depth / 2, width, depth };
}

/**
 * The columns the start courtyard spans, as grid-line indices: `COURTYARD_CELLS` of them
 * centred on the entry gap, slid back inside the maze's width when the gap falls near an edge.
 *
 * Slid rather than overhung: a yard that stuck out past the maze would cross the east or west
 * apron and cut the ring of grass in a second place, which is a different change to the shape of
 * the level. An asymmetric yard is still a yard.
 */
function courtyardColumns(columns: number, entryColumn: number): { left: number; right: number } {
  const cells = Math.min(COURTYARD_CELLS, columns);
  const left = Math.min(Math.max(entryColumn - Math.floor((cells - 1) / 2), 0), columns - cells);
  return { left, right: left + cells };
}

/**
 * The grass a start courtyard encloses: between the side walls' inner faces, and between the
 * maze's own south face and the outer wall. This is the ground the player can stand on inside
 * the yard, which is why `validate` compares the spawn against it.
 */
export function startCourtyardRect(columns: number, rows: number, entryColumn: number): Rect {
  const { left, right } = courtyardColumns(columns, entryColumn);
  const half = HEDGE_THICKNESS / 2;
  return {
    x: -(columns * CELL_SIZE) / 2 + left * CELL_SIZE + half,
    z: (rows * CELL_SIZE) / 2 + half,
    width: (right - left) * CELL_SIZE - HEDGE_THICKNESS,
    depth: APRON_WIDTH - HEDGE_THICKNESS - half,
  };
}

/**
 * The lawn outside the labyrinth that a player may not stand on, as boxes (design D2): the
 * grass the apron spurs and the start courtyard cut off from the spawn, which a player who
 * walks a perimeter crest can drop onto with no ladder to climb back by.
 *
 * Three boxes on the north side always — the apron above the maze and the two side strips
 * down to the spurs' faces at z = ±half a thickness. Four more on the south when the level
 * has a courtyard, because that is what cut the south strips off; with no courtyard the south
 * apron is where the level spawns the player, and none of it is out of bounds. S3 and S4
 * abut the courtyard walls' outer faces exactly, so the yard and its walls stay in bounds,
 * and each is dropped if the yard leaves it no width.
 *
 * Every box runs from `OUT_OF_BOUNDS_BOTTOM` to `CREST_MARGIN` below the hedge crest (D3).
 * A crest-walker stands at the full `hedgeHeight`, above all of them, even over the outer half
 * of the perimeter hedge, so no horizontal inset is wanted or used: stepping off the outside
 * falls through the box and is put back before landing, and an inset would leave a corridor of
 * in-bounds lawn behind the north hedge.
 *
 * No cutout for the gates (D4). A player leaving through one has `beginCrossing` flip the
 * phase, which stops the frame loop, before their feet reach the first box at `-mazeDepth/2`.
 */
export function outOfBoundsLawn(
  columns: number,
  rows: number,
  hedgeHeight: number,
  courtyard: { left: number; right: number } | null,
): OutOfBounds {
  const { x: west, z: north, width, depth } = groundsRect(columns, rows);
  const east = west + width;
  const south = north + depth;
  const mazeWidth = columns * CELL_SIZE;
  const mazeDepth = rows * CELL_SIZE;
  const half = HEDGE_THICKNESS / 2;
  const height = hedgeHeight - CREST_MARGIN - OUT_OF_BOUNDS_BOTTOM;
  const box = (x0: number, z0: number, x1: number, z1: number) =>
    boxBetween({ x: x0, z: z0 }, { x: x1, z: z1 }, OUT_OF_BOUNDS_BOTTOM, height);

  const boxes: Box[] = [
    box(west, north, east, -mazeDepth / 2),
    box(west, -mazeDepth / 2, -mazeWidth / 2, -half),
    box(mazeWidth / 2, -mazeDepth / 2, east, -half),
  ];

  if (courtyard) {
    const xLine = (index: number) => -mazeWidth / 2 + index * CELL_SIZE;
    const westOfYard = xLine(courtyard.left) - half;
    const eastOfYard = xLine(courtyard.right) + half;
    boxes.push(
      box(west, half, -mazeWidth / 2, mazeDepth / 2),
      box(mazeWidth / 2, half, east, mazeDepth / 2),
    );
    if (westOfYard > west) boxes.push(box(west, mazeDepth / 2, westOfYard, south));
    if (eastOfYard < east) boxes.push(box(eastOfYard, mazeDepth / 2, east, south));
  }

  return { boxes, columns, rows };
}

type Run = { from: number; to: number };

/** The maximal runs of consecutive standing segments along one grid line. */
function runsOf(standing: readonly boolean[]): Run[] {
  const runs: Run[] = [];
  let start: number | null = null;
  // One past the end, so a run reaching the last segment is still closed.
  for (let i = 0; i <= standing.length; i += 1) {
    if (standing[i]) {
      if (start === null) start = i;
    } else if (start !== null) {
      runs.push({ from: start, to: i });
      start = null;
    }
  }
  return runs;
}

function boxBetween(
  from: { x: number; z: number },
  to: { x: number; z: number },
  bottom: number,
  height: number,
): Box {
  return {
    center: { x: (from.x + to.x) / 2, y: bottom + height / 2, z: (from.z + to.z) / 2 },
    size: { x: Math.abs(to.x - from.x), y: height, z: Math.abs(to.z - from.z) },
  };
}

export function buildGrounds(
  maze: Maze,
  grounds: GroundsSpec,
  exitObjectiveId: string | null,
): BuiltGrounds {
  const hedgeHeight = grounds.hedgeHeight ?? DEFAULT_HEDGE_HEIGHT;
  const moatWidth = grounds.moatWidth ?? DEFAULT_MOAT_WIDTH;

  const mazeWidth = maze.columns * CELL_SIZE;
  const mazeDepth = maze.rows * CELL_SIZE;
  const xLine = (index: number) => -mazeWidth / 2 + index * CELL_SIZE;
  const zLine = (index: number) => -mazeDepth / 2 + index * CELL_SIZE;

  const hedges: Box[] = [];
  const half = HEDGE_THICKNESS / 2;

  // Hedges running along z, one grid line at a time: verticalHedges[z][x] is the west
  // edge of cell (x, z), and x === columns is the east outer edge.
  for (let x = 0; x <= maze.columns; x += 1) {
    const standing = maze.verticalHedges.map((row) => row[x] === true);
    for (const run of runsOf(standing)) {
      hedges.push(
        boxBetween(
          { x: xLine(x) - half, z: zLine(run.from) },
          { x: xLine(x) + half, z: zLine(run.to) },
          0,
          hedgeHeight,
        ),
      );
    }
  }

  // Hedges running along x: horizontalHedges[z][x] is the north edge of cell (x, z),
  // and z === rows is the south outer edge.
  for (let z = 0; z <= maze.rows; z += 1) {
    const standing = maze.horizontalHedges[z] ?? [];
    for (const run of runsOf(standing)) {
      hedges.push(
        boxBetween(
          { x: xLine(run.from), z: zLine(z) - half },
          { x: xLine(run.to), z: zLine(z) + half },
          0,
          hedgeHeight,
        ),
      );
    }
  }

  const rect = groundsRect(maze.columns, maze.rows);
  const west = rect.x;
  const east = rect.x + rect.width;
  const north = rect.z;
  const south = rect.z + rect.depth;

  // Two more hedges, and the only ones that stand outside the maze: without them the
  // apron is a ring, and the player strolls from the south spawn round the outside to
  // the exit sensor on the north apron having never entered the maze. Each runs from the
  // outer hedge line to the bank at the maze's mid-latitude, overlapping the hedge it
  // meets and reaching the grass edge where the barrier stands, so there is no gap for a
  // 0.3 m capsule to slip through at either end. They go in `hedges` rather than a field
  // of their own: rendering and collision then pick them up as the hedges they are.
  hedges.push(
    boxBetween({ x: west, z: -half }, { x: -mazeWidth / 2 + half, z: half }, 0, hedgeHeight),
    boxBetween({ x: mazeWidth / 2 - half, z: -half }, { x: east, z: half }, 0, hedgeHeight),
  );

  // The start courtyard, when the level asks for one: two walls along z and one across, boxing
  // the entry apron in so the player begins inside the labyrinth rather than on the lawn
  // outside it. Its only opening is the maze's own entry gap, which no run covers.
  //
  // Like the spurs above these are hedges, and they go in `hedges` for the same reason: a
  // courtyard wall *is* a hedge, so rendering, collision and the "spawn is inside a hedge"
  // check all pick it up with nothing new to teach them. The side walls start half a thickness
  // inside the maze's own south run so there is no notch at the join, and the outer wall stands
  // wholly on the grass with its outer face at the grass edge, where the bank already is.
  if (grounds.startCourtyard) {
    const { left, right } = courtyardColumns(maze.columns, maze.entryColumn);
    const southFace = mazeDepth / 2 - half;
    for (const column of [left, right]) {
      hedges.push(
        boxBetween(
          { x: xLine(column) - half, z: southFace },
          { x: xLine(column) + half, z: south },
          0,
          hedgeHeight,
        ),
      );
    }
    hedges.push(
      boxBetween(
        { x: xLine(left) - half, z: south - HEDGE_THICKNESS },
        { x: xLine(right) + half, z: south },
        0,
        hedgeHeight,
      ),
    );
  }

  // The moat is a ring of four panels, the long two taking the corners so the water has
  // no notch in it. Their top face is the surface the player looks down at.
  const waterBottom = -WATER_DROP - WATER_THICKNESS;
  const waterPanels: Box[] = [
    boxBetween(
      { x: west - moatWidth, z: north - moatWidth },
      { x: east + moatWidth, z: north },
      waterBottom,
      WATER_THICKNESS,
    ),
    boxBetween(
      { x: west - moatWidth, z: south },
      { x: east + moatWidth, z: south + moatWidth },
      waterBottom,
      WATER_THICKNESS,
    ),
    boxBetween({ x: west - moatWidth, z: north }, { x: west, z: south }, waterBottom, WATER_THICKNESS),
    boxBetween({ x: east, z: north }, { x: east + moatWidth, z: south }, waterBottom, WATER_THICKNESS),
  ];

  // The bank: each barrier's inner face is flush with the edge of the grass, so the stop
  // lands where the water starts rather than a stride short of it. The north and south
  // pair run long to close the corners.
  const barriers: Box[] = [
    boxBetween(
      { x: west - BARRIER_THICKNESS, z: north - BARRIER_THICKNESS },
      { x: east + BARRIER_THICKNESS, z: north },
      0,
      BARRIER_HEIGHT,
    ),
    boxBetween(
      { x: west - BARRIER_THICKNESS, z: south },
      { x: east + BARRIER_THICKNESS, z: south + BARRIER_THICKNESS },
      0,
      BARRIER_HEIGHT,
    ),
    boxBetween({ x: west - BARRIER_THICKNESS, z: north }, { x: west, z: south }, 0, BARRIER_HEIGHT),
    boxBetween({ x: east, z: north }, { x: east + BARRIER_THICKNESS, z: south }, 0, BARRIER_HEIGHT),
  ];

  // The trodden path: one leg between the centres of every pair of cells with no hedge
  // between them, so the mud lies exactly where the maze can be walked — plus a stub out
  // of each mouth, straight across the apron to the grass edge, so the path arrives from
  // the bank and leaves to it instead of starting at a wall of hedge.
  const cellCentre = (x: number, z: number) => ({
    x: xLine(x) + CELL_SIZE / 2,
    z: zLine(z) + CELL_SIZE / 2,
  });
  const paths: PathSegment[] = [];
  for (let z = 0; z < maze.rows; z += 1) {
    for (let x = 0; x < maze.columns; x += 1) {
      if (x + 1 < maze.columns && !maze.verticalHedges[z]![x + 1]) {
        paths.push({ from: cellCentre(x, z), to: cellCentre(x + 1, z) });
      }
      if (z + 1 < maze.rows && !maze.horizontalHedges[z + 1]![x]) {
        paths.push({ from: cellCentre(x, z), to: cellCentre(x, z + 1) });
      }
    }
  }
  paths.push(
    {
      from: cellCentre(maze.entryColumn, maze.rows - 1),
      to: { x: cellCentre(maze.entryColumn, 0).x, z: south },
    },
    {
      from: cellCentre(maze.exitColumn, 0),
      to: { x: cellCentre(maze.exitColumn, 0).x, z: north },
    },
  );

  // The gates: one standing in each gap in the outer hedge, filling it from the grass to
  // the crest. A hedge run ends on the grid line, so the gap is exactly one cell wide —
  // the gate takes that plus half a thickness of overlap either side, which leaves no
  // crack of daylight and nothing for a capsule to squeeze past at the edge. They are
  // never collidable (see `collidableBoxes`): the hedges either side already stop the
  // player everywhere but the opening, and the opening is what the gate fills, so the
  // only way past is through — without the gate itself being something to catch on.
  const gate = (column: number, z: number) =>
    boxBetween(
      { x: xLine(column) - half, z: z - GATE_DEPTH / 2 },
      { x: xLine(column + 1) + half, z: z + GATE_DEPTH / 2 },
      0,
      hedgeHeight,
    );
  const exitGate = gate(maze.exitColumn, -mazeDepth / 2);
  const entryGate = gate(maze.entryColumn, mazeDepth / 2);

  // Where a player who has just walked through one of these gates is put down: inside the
  // maze, in the cell that gate opens into, facing the maze with the gate behind them.
  //
  // Inside, and not on the apron outside, because the gate fills the only gap at its end
  // of the maze. A player set down outside it stands in a pocket with one way out — the
  // gate they just came through — so their first step forward crosses straight back and
  // the level they arrived in is never seen. Landing them past it leaves the maze ahead
  // and the way back one step behind, which is what walking through a door does.
  const exitArrival = {
    position: { x: exitGate.center.x, y: 0, z: -mazeDepth / 2 + GATE_CLEARANCE },
    yaw: Math.PI,
  };
  const entryArrival = {
    position: { x: entryGate.center.x, y: 0, z: mazeDepth / 2 - GATE_CLEARANCE },
    yaw: 0,
  };

  // Ladders come last: they lean on the hedges above, so they need the height those
  // were built at.
  const ladders = placeLadders(maze, grounds.seed, grounds.ladderCount ?? 0, hedgeHeight);

  // Tables stand in the dead ends, on the ground and clear of the hedges, so unlike the
  // ladders they need nothing from the geometry above.
  const tables = placeTables(maze, grounds.seed, grounds.tableCount ?? 0);

  const courtyard = grounds.startCourtyard
    ? courtyardColumns(maze.columns, maze.entryColumn)
    : null;

  return {
    hedges,
    bounds: outOfBoundsLawn(maze.columns, maze.rows, hedgeHeight, courtyard),
    lawn: rect,
    waterPanels,
    barriers,
    ladders,
    tables,
    exitGate,
    entryGate,
    exitArrival,
    entryArrival,
    exitObjectiveId,
    hedgeHeight,
    paths,
  };
}
